import { ReactNode, useEffect, useState } from "react";
import PrintPage from "@components/print/PrintElements/PrintPage";
import { useAppSelector } from "@/store/hooks";
import moment from "moment";
import cx from "classnames";
import styles from "./style.module.scss";
import LoadingScreen from "@components/content/LoadingScreen";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { formatCant } from "@utils/Cant";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import { CategoryApi } from "@/api/CategoryApi";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { formatMoneyValue } from "@utils/Currency";

type InventoryReportPrintProps = {
  excelExport?: boolean
}

type InventoryCategory = {
  title: string,
  products: ProductDataType[]
}

const InventoryReportPrint = (props: InventoryReportPrintProps) => {
  const suscription = useAppSelector((state) => { return state.suscription.suscription || {} });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);

  useEffect(() => {
    import("@printerVariation/generic.scss");
  }, []);

  useEffect(() => {
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadReportData()
  }, []);

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      const categories: InventoryCategory[] = []
      const categoryList = await CategoryApi.optionsList({ activo: { eq: true } });
      for (const categoryId in categoryList) {
        let nt = undefined;
        let products: ProductDataType[] = [];
        do {
          const { items, nextToken } = await ProductApi.fetchAllByCategory(categoryId, {}, `${nt || ""}`, 1000,
            ["codigo", "descripcion_fact", "temporal", "activo", "existencias", "costo"]
          )
          products = [
            ...products,
            ...(items.filter(p => !p.temporal && p.activo))
          ]
          nt = nextToken
        } while (nt)

        products.sort((a, b) => {
          const nameA = a.descripcion_fact || "";
          const nameB = b.descripcion_fact || "";

          return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
        })

        categories.push({
          title: categoryList[categoryId],
          products
        })
      }

      setCategories(categories);
    } finally {
      setIsLoading(false);
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  const renderCategories = (): ReactNode[] => {
    return categories.map(c => {
      return (<div>
        <div className={styles.categoryTitle}>{c.title}</div>
        <table className={styles.productTable}>
          <tbody>
            {c.products.map(p => {
              return (
                <tr className={styles.productRows}>
                  <td className={styles.codigo}>{p.codigo}</td>
                  <td className={styles.description}>
                    {p.descripcion_fact}
                  </td>
                  <td className={styles.costo}>{formatMoneyValue(p.costo, 1)}</td>
                  <td className={styles.existencias}>{formatCant(p.existencias)}</td>
                  <td className={styles.verificacion}>&nbsp;</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>)
    })
  }

  return (
    <div>
      <PrintPage
        autoPrint={false}
        onReadyDebounce={2000}
      >
        <table className={styles.pageContent}>
          <thead>
            <tr>
              <th>
                <div className={styles.printHeader}>
                  <PrintPageHeader suscription={suscription} />
                  <table className={cx([styles.productTable, "mt-2"])}>
                    <tbody>
                      <tr className={styles.productRows}>
                        <td className={styles.codigo}>Codigo</td>
                        <td className={styles.description}>Descripcion</td>
                        <td className={styles.costo}>Costo</td>
                        <td className={styles.existencias}>Und</td>
                        <td className={styles.verificacionHeader}>&nbsp;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {renderCategories()}
          </tbody>
        </table>
      </PrintPage>

      <LoadingScreen isLoading={isLoading} />

    </div>
  );
}

export default InventoryReportPrint;
